import { JWMHeaderHero } from "@components/JWMHeaderHero/JWMHeaderHero";
import { PageContainer } from "@components/PageContainer/PageContainer";
import getTranslation from "@components/RecipeGrid/util/getTranslation";
import { useQueryStringStorage } from "@hooks/useQueryStringStorage/useQueryStringStorage";
import { useLocale } from "@jmc/core/src/hooks/useLocale/index";
import Separator from "@jmc/solid-design-system/src/components/atoms/Separator/Separator";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { TwoColumnLayout } from "@jmc/solid-design-system/src/components/organisms/TwoColumnLayout/TwoColumnLayout";
import { BreakPoint, useMediaQuery } from "@jmc/solid-design-system/src/hooks/useMediaQuery/useMediaQuery";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { CMSRecipe } from "@jwm/types/CMSRecipe";
import { CMSService } from "@jwm/types/CMSService";
import { useLocation } from "@reach/router";
import { graphql } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import React from "react";
import { useTranslation } from "react-i18next";

import { BackButton } from "./blocks/BackButton";
import { IngredientsList } from "./blocks/IngredientsList";
import { Methods } from "./blocks/Methods";
import { NutritionFacts } from "./blocks/NutritionFacts";
import { RecipeDetails } from "./blocks/RecipeDetails";
import { RelatedRecipes } from "./blocks/RelatedRecipes";
import { SocialMediaBlock } from "./blocks/SocialMediaBlock";
import { TagList } from "./blocks/TagList";
import style from "./style.module.scss";
import { RecipeProps } from "./types/RecipeProps";

interface Props {
    data: {
        page: RecipeProps;
        recipes: { nodes: CMSRecipe[] };
        service: CMSService;
        translations: {
            nodes: {
                title: string;
                translation: string;
            }[];
        };
    };
}

const RecipePage = ({ data }: Props): JSX.Element => {
    const { jnjFullBranded } = useJnjBranding();
    const { t } = useTranslation();
    const qss = useQueryStringStorage();
    const locationState = useLocation()?.state as { anchor?: string; origin?: string; hashKey?: string };
    const { specialty, general, image } = data.page;

    const dietary_wishes = general?.dietary_wishes;
    const course = general?.course;
    const difficulty = general?.difficulty;
    const specialties = specialty.map(({ title }) => title);
    const pageTitle = data?.page?.title;
    const locale = useLocale();

    const tags = [...specialties, ...dietary_wishes, ...course, difficulty];
    const haveOrigin = locationState?.origin ? true : false;
    const haveHashKey = locationState?.hashKey ? true : false;
    const serviceEnabled = data?.service?.service_enabled;
    const imageTitle = image?.description || image?.title?.split(".").shift() || "";
    const isMobile = useMediaQuery(BreakPoint.lg);

    let urlString = "";
    if (haveOrigin && haveHashKey) {
        const backURL = new URL(locationState?.origin);
        const qs = qss.getQS(locationState?.hashKey);
        qs.forEach((value, key) => {
            backURL.searchParams.append(key, value);
        });

        // Set urlString
        const path = backURL.pathname.replace(`/${locale}`, "");
        urlString = `${path}${backURL.search}`;
    } else if (serviceEnabled) {
        urlString = `${data?.service?.url}`;
    }

    const RecipeImage = (): JSX.Element => {
        return (
            <>
                <GatsbyImage
                    className={style.recipeImage}
                    image={data?.page?.image?.gatsbyImageData}
                    style={{ maxHeight: "512px" }}
                    alt={pageTitle}
                    title={imageTitle}
                />
                {data?.page?.image_copyright && (
                    <div className={style.copyright}>
                        <Typography size="xs" font="title" weight="400">
                            {data?.page?.image_copyright}
                        </Typography>
                    </div>
                )}
            </>
        );
    };

    return jnjFullBranded ? (
        <TwoColumnLayout
            detailContentId="bodycontent"
            asideContent={
                <>
                    {!isMobile && (
                        <RecipeDetails recipeDetails={data?.page?.recipe_details} generalData={data?.page?.general} />
                    )}
                    <TagList
                        tags={tags}
                        recipeLiterals={data?.translations?.nodes}
                        haveOrigin={haveOrigin}
                        haveHashKey={haveHashKey}
                        serviceEnabled={serviceEnabled}
                        serviceUrl={data?.service?.url}
                        locationState={locationState}
                    />
                    <SocialMediaBlock
                        socialMediaOptions={data?.service?.social_media_sharing_options}
                        pageTitle={pageTitle}
                    />
                </>
            }
            pageHeader={
                <>
                    <BackButton urlString={urlString} />
                    <JWMHeaderHero id={`Recipe.${pageTitle}`} title={pageTitle} />
                </>
            }
            relatedArticles={<RelatedRecipes pageTitle={pageTitle} relatedRecipes={data?.recipes?.nodes} />}
            footer={null}
            showOneColumnSeparator
        >
            <>
                <RecipeImage />
                {isMobile && (
                    <RecipeDetails recipeDetails={data?.page?.recipe_details} generalData={data?.page?.general} />
                )}
                <IngredientsList ingredients={data?.page?.ingredients} />
                <Methods methods={data?.page?.method} />
                <NutritionFacts nutritionalFacts={data?.page?.nutritional_facts} />
            </>
        </TwoColumnLayout>
    ) : (
        <PageContainer backgroundColor="white">
            <BackButton urlString={urlString} />
            <div className={style.title}>
                <Typography variant="h1" color="dark" size="4xl" font="title" weight="500">
                    {pageTitle}
                </Typography>
            </div>
            <TagList
                tags={tags}
                recipeLiterals={data?.translations?.nodes}
                haveOrigin={haveOrigin}
                haveHashKey={haveHashKey}
                serviceEnabled={serviceEnabled}
                serviceUrl={data?.service?.url}
                locationState={locationState}
            />
            <RecipeImage />
            <div className={style.details}>
                <div className={style.serves}>
                    <Typography size="l" font="title" weight="800">
                        {t("Serves", { ns: "recipe" }).toUpperCase()}
                    </Typography>
                    <Typography size="m" font="text" weight="400">
                        {data?.page?.recipe_details?.serves}
                    </Typography>
                </div>
                <div className={style.totalTime}>
                    <Typography size="l" font="title" weight="800">
                        {t("Total time", { ns: "recipe" }).toUpperCase()}
                    </Typography>
                    <Typography size="m" font="text" weight="400">
                        {data?.page?.recipe_details?.total_time === "1"
                            ? t("{{TOTAL_TIME}} Minute", {
                                  TOTAL_TIME: data?.page?.recipe_details?.total_time,
                                  ns: "recipe",
                              })
                            : t("{{TOTAL_TIME}} Minutes", {
                                  TOTAL_TIME: data?.page?.recipe_details?.total_time,
                                  ns: "recipe",
                              })}
                    </Typography>
                </div>
                <div className={style.course}>
                    <Typography size="l" font="title" weight="800">
                        {t("Course", { ns: "recipe" }).toUpperCase()}
                    </Typography>
                    <Typography size="m" font="text" weight="400">
                        {data?.page?.general?.course.map((course, index) => {
                            const length = data?.page?.general?.course.length;
                            if (index === length - 1) {
                                return getTranslation(course, t);
                            }
                            return `${getTranslation(course, t)}, `;
                        })}
                    </Typography>
                </div>
                <div className={style.difficulty}>
                    <Typography size="l" font="title" weight="800">
                        {t("Difficulty", { ns: "recipe" }).toUpperCase()}
                    </Typography>
                    <Typography size="m" font="text" weight="400">
                        {getTranslation(data?.page?.general?.difficulty, t)}
                    </Typography>
                </div>
            </div>
            <IngredientsList ingredients={data?.page?.ingredients} />
            <Methods methods={data?.page?.method} />
            <NutritionFacts nutritionalFacts={data?.page?.nutritional_facts} />
            <Separator color="grey" />
            <SocialMediaBlock socialMediaOptions={data?.service?.social_media_sharing_options} pageTitle={pageTitle} />
            <RelatedRecipes pageTitle={pageTitle} relatedRecipes={data?.recipes?.nodes} />
        </PageContainer>
    );
};

export default RecipePage;

export const query = graphql`
    query (
        $locale: String!
        $url: String!
        $course: [String]
        $dietary_wishes: [String]
        $difficulty: String
        $specialties: [String]
    ) {
        page: contentstackRecipe(publish_details: { locale: { eq: $locale } }, url: { eq: $url }) {
            ...recipeFields
        }
        recipes: allContentstackRecipe(
            limit: 10
            filter: {
                publish_details: { locale: { eq: $locale } }
                general: {
                    course: { in: $course }
                    dietary_wishes: { in: $dietary_wishes }
                    difficulty: { eq: $difficulty }
                }
                specialty: { elemMatch: { id: { in: $specialties } } }
                url: { ne: $url }
            }
        ) {
            nodes {
                ...recipeCardFields
            }
        }
        service: contentstackRecipeService(publish_details: { locale: { eq: $locale } }) {
            service_enabled
            url
            imr_number
            updated_at
            short_disclaimer
            long_disclaimer
            social_media_sharing_options {
                title
                platforms {
                    facebook {
                        message
                        share
                    }
                    linkedin {
                        share
                    }
                    twitter {
                        message
                        share
                    }
                    email {
                        subject
                        body
                        share
                    }
                }
            }
        }
        translations: allContentstackLiteral(
            filter: { context: { eq: "Recipe" }, publish_details: { locale: { eq: $locale } } }
        ) {
            nodes {
                title
                translation
            }
        }
    }
`;
